// MobileInstrumentSearch.jsx
// Phone-native replacement for the desktop InstrumentSelector modal when adding
// an instrument. A full-screen search surface (slides up): big search field,
// type pills, live (debounced) results, tap a row to add. Reuses the SAME
// search API (services.instrumentMaster.search) and the SAME onSelect contract
// ({ instrument }) so the parent's F&O / continuous-contract flow is unchanged.
import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Sheet } from "../ds";
import { services } from "../../services/api";

const mono = { fontFamily: "var(--font-mono, ui-monospace, monospace)", fontVariantNumeric: "tabular-nums" };

const TYPE_PILLS = [
  { label: "All", value: "" },
  { label: "Equity", value: "EQUITY" },
  { label: "Futures", value: "FUTURE" },
  { label: "Options", value: "OPTION" },
];

function fmtExpiry(d) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return null; }
}

export default function MobileInstrumentSearch({ open, onClose, onSelect }) {
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Reset everything when closed.
  useEffect(() => {
    if (!open) { setQ(""); setType(""); setResults([]); setTotal(0); }
  }, [open]);

  // Focus the field shortly after the sheet mounts (after the slide-in).
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Debounced live search (also runs on open to browse). Guards against
  // out-of-order responses with a request id.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = { symbol: q.trim(), page: 1, limit: 50 };
        if (type) params.instrument_type = type;
        const res = await services.instrumentMaster.search(params);
        if (cancelled) return;
        setResults(res.data?.instruments || []);
        setTotal(res.data?.total || 0);
      } catch (e) {
        if (!cancelled) { setResults([]); setTotal(0); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, type, open]);

  const pick = (inst) => onSelect?.({ instrument: inst });

  return (
    <Sheet open={open} onClose={onClose} detent="full">
          {/* Search bar row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 12px 8px",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "var(--bg-2)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: "0 12px",
                height: 44,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--fg-3)" strokeWidth="2">
                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
              </svg>
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search NSE, BSE, F&O…"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                style={{
                  flex: 1,
                  height: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  color: "var(--fg-1)",
                  fontSize: 15,
                }}
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  aria-label="Clear"
                  style={{ appearance: "none", border: "none", background: "transparent", cursor: "pointer", color: "var(--fg-3)", display: "flex" }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></svg>
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{ appearance: "none", border: "none", background: "transparent", cursor: "pointer", color: "var(--color-accent)", fontSize: 15, fontWeight: 600, padding: "0 4px", flex: "0 0 auto" }}
            >
              Cancel
            </button>
          </div>

          {/* Type filter pills */}
          <div style={{ display: "flex", gap: 6, overflowX: "auto", padding: "10px 12px", borderBottom: "1px solid var(--border)" }}>
            {TYPE_PILLS.map((t) => {
              const active = type === t.value;
              return (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => setType(t.value)}
                  style={{
                    appearance: "none", cursor: "pointer", flex: "0 0 auto",
                    fontSize: 12, fontWeight: 500, whiteSpace: "nowrap",
                    padding: "5px 12px", borderRadius: 999,
                    // Neutral filter treatment — matches MobileWatchList's
                    // type-filter pills (recessed, no accent).
                    color: active ? "var(--fg-1)" : "var(--fg-3)",
                    background: active ? "var(--bg-3)" : "transparent",
                    border: "1px solid var(--border)",
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Results */}
          <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
            {loading && results.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "var(--fg-3)", fontSize: 13 }}>Searching…</div>
            ) : results.length === 0 ? (
              <div style={{ padding: "56px 24px", textAlign: "center", color: "var(--fg-3)" }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--fg-2)", marginBottom: 4 }}>No instruments</div>
                <div style={{ fontSize: 12 }}>Try a symbol like RELIANCE or NIFTY.</div>
              </div>
            ) : (
              <>
                <div style={{ padding: "8px 16px 4px", fontSize: 10, color: "var(--fg-3)", letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 700 }}>
                  {total} result{total === 1 ? "" : "s"}{results.length < total ? ` · showing ${results.length}` : ""}
                </div>
                {results.map((inst) => {
                  const exp = fmtExpiry(inst.expiry_date);
                  return (
                    <motion.button
                      key={inst.instrument_id}
                      type="button"
                      whileTap={{ backgroundColor: "rgba(var(--color-accent-rgb), 0.08)" }}
                      onClick={() => pick(inst)}
                      style={{
                        appearance: "none", width: "100%", textAlign: "left", cursor: "pointer",
                        background: "transparent", border: "none",
                        borderBottom: "1px solid var(--border)",
                        padding: "12px 16px",
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                        color: "var(--fg-1)",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 14, fontWeight: 600 }}>{inst.symbol}</span>
                          <span style={{ fontSize: 9, fontWeight: 600, color: "var(--fg-3)", border: "1px solid var(--border)", padding: "1px 5px", borderRadius: 3, letterSpacing: ".05em" }}>{inst.exchange}</span>
                          {inst.instrument_type && (
                            <span style={{ fontSize: 9, fontWeight: 600, color: "var(--fg-3)" }}>{inst.instrument_type}</span>
                          )}
                          {exp && <span style={{ ...mono, fontSize: 9, color: "var(--fg-3)" }}>{exp}</span>}
                          {inst.strike_price ? <span style={{ ...mono, fontSize: 9, color: "var(--fg-3)" }}>₹{inst.strike_price}</span> : null}
                          {inst.option_type ? <span style={{ fontSize: 9, fontWeight: 700, color: inst.option_type === "CE" ? "var(--color-profit)" : "var(--color-loss)" }}>{inst.option_type}</span> : null}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 240 }}>
                          {inst.name}
                        </div>
                      </div>
                      <span style={{ flex: "0 0 auto", width: 28, height: 28, borderRadius: 8, background: "rgba(var(--color-accent-rgb), 0.14)", color: "var(--color-accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, lineHeight: 1 }}>+</span>
                    </motion.button>
                  );
                })}
              </>
            )}
          </div>
    </Sheet>
  );
}
